import React, { useState, useMemo, useRef, useEffect, createContext, useContext, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Calculator, GitCompareArrows, Check, ChevronRight, Clock, Phone, AlertCircle, Sparkles, ArrowRight, Hand, MousePointerClick, MoveHorizontal, Trophy, X } from "lucide-react";
import { LeadCtaButtons, BookDemoModal } from "./LeadModals";
import { trackLanding } from "../lib/analytics";

/**
 * Three high-engagement interactive demos for Landing:
 *  1. ThreeTapPhoneDemo — fake smartphone, click servizio→giorno→ora→done in <30s
 *  2. RoiCalculator — input appt/week → live output of hours/euros saved
 *  3. BeforeAfterSlider — drag a vertical slider between "old way" agenda
 *     (chaotic) and "with Prenotoo" (clean) — visceral narrative
 *
 * Each demo includes an InteractionHint that appears when the section enters the
 * viewport and disappears at the first user interaction (or after 8s as fallback).
 *
 * A shared <DemoProgressProvider> tracks how many of the 3 demos the visitor has
 * explored. The floating <DemoProgressTracker /> badge appears bottom-right after
 * the first interaction, increments dot-by-dot, and morphs into a celebratory
 * CTA card when all 3 are completed.
 */
export default function HomeShowcase() {
  return (
    <DemoProgressProvider>
      <ThreeTapPhoneDemo />
      <RoiCalculator />
      <BeforeAfterSlider />
      <DemoProgressTracker />
    </DemoProgressProvider>
  );
}

// ---------- Progress tracking ----------
const DemoProgressCtx = createContext(null);
const STORAGE_KEY = "prenotoo_demo_explored_v1";
const ALL_KEYS = ["3tap", "roi", "ba"];

function DemoProgressProvider({ children }) {
  const [explored, setExplored] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();
  });
  const [dismissed, setDismissed] = useState(false);
  const [roiSnapshot, setRoiSnapshot] = useState(null);

  const markExplored = useCallback((key) => {
    setExplored((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    explored, markExplored,
    count: explored.size,
    total: ALL_KEYS.length,
    complete: explored.size >= ALL_KEYS.length,
    dismissed, setDismissed,
    roiSnapshot, setRoiSnapshot,
  }), [explored, markExplored, dismissed, roiSnapshot]);

  return <DemoProgressCtx.Provider value={value}>{children}</DemoProgressCtx.Provider>;
}

function useDemoProgress() {
  return useContext(DemoProgressCtx) || { markExplored: () => {}, explored: new Set(), count: 0, total: 3, complete: false, setRoiSnapshot: () => {}, roiSnapshot: null };
}

// Reads the cookie-consent localStorage key on mount + listens for changes.
// Mirrors CookieBanner.jsx exactly: key 'prenotoo-cookie-consent', version 3.
function useCookieBannerVisible() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const check = () => {
      try {
        const raw = localStorage.getItem("prenotoo-cookie-consent");
        if (!raw) { setVisible(true); return; }
        const v = JSON.parse(raw);
        setVisible(!v || v.v !== 3);
      } catch { setVisible(true); }
    };
    check();
    // Re-check periodically until the user dismisses the banner. Once cookie
    // consent is recorded (key set), we can stop polling.
    const id = setInterval(check, 1000);
    window.addEventListener("storage", check);
    return () => { clearInterval(id); window.removeEventListener("storage", check); };
  }, []);
  return visible;
}

// ---------- Shared helpers ----------
function useInView(threshold = 0.4) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setSeen(true); io.disconnect(); break; }
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
}

/**
 * Floating hint that wobbles to grab attention. Auto-dismisses on interaction
 * (caller toggles `dismissed`) or after `autoHideMs` (default 9s) so it never
 * lingers if the user never interacts.
 */
function InteractionHint({ visible, text, icon: Icon = Hand, position = "top", tone = "blue", testid, autoHideMs = 9000 }) {
  const [autoHidden, setAutoHidden] = useState(false);
  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(() => setAutoHidden(true), autoHideMs);
    return () => clearTimeout(t);
  }, [visible, autoHideMs]);
  const show = visible && !autoHidden;
  const tones = {
    blue: "bg-blue-600 text-white",
    emerald: "bg-emerald-600 text-white",
    rose: "bg-rose-600 text-white",
    dark: "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900",
  };
  const positions = {
    top: "top-2 left-1/2 -translate-x-1/2",
    "top-right": "top-3 right-3",
    "top-left": "top-3 left-3",
    bottom: "bottom-3 left-1/2 -translate-x-1/2",
  };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.9 }}
          animate={{
            opacity: 1, y: 0, scale: 1,
            // Subtle wobble loop — rotation feels playful without being annoying
            rotate: [0, -2.5, 2.5, -1.5, 1.5, 0],
          }}
          transition={{
            opacity: { duration: 0.3 },
            y: { duration: 0.3 },
            scale: { duration: 0.3 },
            rotate: { duration: 1.6, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" },
          }}
          exit={{ opacity: 0, y: -6, scale: 0.9, transition: { duration: 0.25 } }}
          className={`absolute ${positions[position]} z-40 ${tones[tone]} px-3 py-1.5 rounded-full shadow-lg shadow-black/20 text-[11px] font-semibold tracking-wide inline-flex items-center gap-1.5 whitespace-nowrap pointer-events-none`}
          data-testid={testid}
        >
          <Icon className="h-3.5 w-3.5" />
          <span>{text}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// =====================================================================
// PROGRESS TRACKER (floating, bottom-right)
// =====================================================================

const DEMO_LABELS = { "3tap": "3-tap demo", roi: "Calcolatore", ba: "Prima · Dopo" };

function DemoProgressTracker() {
  const { count, total, complete, explored, dismissed, setDismissed, roiSnapshot } = useDemoProgress();
  const [bookOpen, setBookOpen] = useState(false);
  const cookieVisible = useCookieBannerVisible();
  // Fire opened_celebration once when complete first becomes true and tracker visible
  useEffect(() => { if (complete && !dismissed) trackLanding("opened_celebration"); }, [complete, dismissed]);
  // Wait for first interaction before mounting the floating chip
  if (count === 0 || dismissed) return null;

  // Cookie banner sits at bottom on most viewports — push tracker up so it
  // doesn't overlap. Mobile cookie banner is taller (~140px), desktop ~100px.
  const positionCls = cookieVisible
    ? "bottom-44 sm:bottom-28 right-3 sm:right-5"
    : "bottom-5 right-5";

  // Build a personalized headline if the visitor moved the ROI sliders
  const fmtEur = (n) => Math.round(n).toLocaleString("it-IT");
  const personalized = roiSnapshot && roiSnapshot.eurYear >= 500
    ? `Stai per recuperare circa € ${fmtEur(roiSnapshot.eurYear)} all'anno`
    : null;

  return (
    <>
      <AnimatePresence>
        {!complete && (
          <motion.div
            key="chip"
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className={`fixed ${positionCls} z-50 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-full shadow-2xl pl-4 pr-2 py-2 inline-flex items-center gap-3 max-w-[calc(100vw-40px)] transition-[bottom] duration-300`}
            data-testid="demo-progress-chip"
          >
            <Sparkles className="h-3.5 w-3.5 text-amber-400 dark:text-amber-500 shrink-0" />
            <div className="leading-tight">
              <div className="text-[10px] font-semibold tracking-widest uppercase opacity-70">Hai esplorato</div>
              <div className="text-sm font-semibold tabular-nums">
                {count} <span className="opacity-60">/ {total} demo</span>
              </div>
            </div>
            <div className="flex items-center gap-1 ml-1">
              {ALL_KEYS.map((k) => (
                <div
                  key={k}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${explored.has(k) ? "bg-emerald-400 dark:bg-emerald-600" : "bg-white/25 dark:bg-zinc-300"}`}
                />
              ))}
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="h-7 w-7 rounded-full hover:bg-white/10 dark:hover:bg-zinc-200 flex items-center justify-center opacity-60 hover:opacity-100"
              aria-label="Nascondi"
              data-testid="demo-progress-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}

        {complete && (
          <motion.div
            key="celebration"
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.92 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className={`fixed ${positionCls} z-50 max-w-[calc(100vw-40px)] sm:max-w-sm bg-white dark:bg-zinc-950 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden transition-[bottom] duration-300`}
            data-testid="demo-progress-celebration"
          >
            {/* gradient header */}
            <div className="relative bg-gradient-to-br from-emerald-500 via-blue-500 to-violet-500 p-5 text-white">
              <button
                onClick={() => setDismissed(true)}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Chiudi"
                data-testid="demo-celebration-close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 250, damping: 14, delay: 0.1 }}
                className="h-12 w-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-3"
              >
                <Trophy className="h-6 w-6" />
              </motion.div>
              <div className="font-display text-lg font-semibold leading-tight" data-testid="demo-celebration-title">
                {personalized ? personalized : "Hai visto tutto Prenotoo!"}
              </div>
              <div className="text-xs text-white/85 mt-1">
                {personalized
                  ? "Hai esplorato tutti i demo. Parliamone dal vivo per renderlo concreto."
                  : "3 demo su 3 esplorate · È il momento di parlarne dal vivo."}
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-3">
                {personalized
                  ? `Sulla base della tua attività (${Math.round(roiSnapshot.apptPerWeek)} appuntamenti/sett, ${roiSnapshot.recoveredApptYear ? `+${Math.round(roiSnapshot.recoveredApptYear)} prenotazioni recuperate/anno` : "valore tempo recuperato"}), una demo di 30 minuti ti basta per vedere il setup sul tuo caso.`
                  : "Ti facciamo vedere come funziona sulla tua attività in 30 minuti, gratis."}
              </p>
              <button
                type="button"
                onClick={() => { trackLanding("clicked_celebration_cta"); setBookOpen(true); }}
                className="btn-accent w-full justify-center"
                data-testid="demo-celebration-cta"
              >
                Prenota la tua demo <ArrowRight className="h-4 w-4 ml-2" />
              </button>
              <button
                onClick={() => { trackLanding("dismissed_celebration"); setDismissed(true); }}
                className="mt-2 w-full text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 py-1"
                data-testid="demo-celebration-later"
              >
                Più tardi
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <BookDemoModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </>
  );
}

// =====================================================================
// 1. THREE-TAP PHONE DEMO
// =====================================================================

const SERVICES = [
  { id: "manicure", name: "Manicure classica", duration: "45 min", price: "€ 25" },
  { id: "pedicure", name: "Pedicure estetica", duration: "60 min", price: "€ 35" },
  { id: "viso", name: "Pulizia viso", duration: "75 min", price: "€ 50" },
  { id: "massaggio", name: "Massaggio rilassante", duration: "50 min", price: "€ 45" },
];

const DAYS = ["Lun 17", "Mar 18", "Mer 19", "Gio 20", "Ven 21", "Sab 22"];
const SLOTS_BY_DAY = {
  "Lun 17": ["09:30", "10:30", "11:30", "14:00", "15:00", "16:30"],
  "Mar 18": ["09:00", "10:00", "11:00", "14:30", "15:30", "17:00"],
  "Mer 19": ["09:30", "10:30", "12:00", "14:00", "16:00", "17:30"],
  "Gio 20": ["09:00", "11:00", "12:00", "14:30", "16:30", "18:00"],
  "Ven 21": ["09:00", "10:30", "11:30", "14:00", "15:00", "17:00"],
  "Sab 22": ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00"],
};

function ThreeTapPhoneDemo() {
  const [step, setStep] = useState(0); // 0=service, 1=day, 2=slot, 3=done
  const [picked, setPicked] = useState({ service: null, day: null, slot: null });
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const tickRef = useRef(null);

  const { markExplored } = useDemoProgress();

  useEffect(() => {
    if (running) {
      tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else if (tickRef.current) {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [running]);

  function pick(key, value, nextStep) {
    if (!running) setRunning(true);
    markExplored("3tap");
    setPicked((p) => ({ ...p, [key]: value }));
    setStep(nextStep);
    if (nextStep === 3) { setRunning(false); trackLanding("completed_3tap", { seconds }); }
  }

  function reset() {
    setStep(0);
    setPicked({ service: null, day: null, slot: null });
    setSeconds(0);
    setRunning(false);
  }

  const stepLabel = ["1. Servizio", "2. Giorno", "3. Orario", "Fatto!"][step];
  const [phoneRef, phoneInView] = useInView(0.35);
  const userInteracted = step > 0;
  useEffect(() => { if (phoneInView) trackLanding("viewed_3tap"); }, [phoneInView]);

  return (
    <section className="relative bg-zinc-50 dark:bg-zinc-950 border-y border-zinc-100 dark:border-zinc-800 overflow-hidden" data-testid="home-3tap-demo" ref={phoneRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center">
        {/* Left: copy + stopwatch */}
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-3 dark:text-blue-400 inline-flex items-center gap-1.5">
            <Smartphone className="h-3 w-3" /> Provala adesso
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium dark:text-zinc-100">
            Prenotare in <em className="not-italic text-blue-600 dark:text-blue-400">3 tap</em>.<br/>
            Niente di più.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 mt-4 dark:text-zinc-400 max-w-md">
            Questa è esattamente l'esperienza che i tuoi clienti vivono. Servizio, giorno, orario.
            Niente registrazione obbligatoria, nessun campo inutile, nessuna telefonata.
          </p>

          <div className="mt-8 inline-flex items-center gap-3 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 px-5 py-3.5">
            <div className="h-10 w-10 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-400">{stepLabel}</div>
              <div className="font-display text-2xl tabular-nums dark:text-zinc-100" data-testid="3tap-stopwatch">
                {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
              </div>
            </div>
            {step === 3 && (
              <div className="ml-3 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 text-xs font-semibold">
                Prenotato in {seconds}s ⚡
              </div>
            )}
          </div>

          {step === 3 && (
            <div className="mt-4">
              <button onClick={reset} className="text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 underline" data-testid="3tap-reset">
                Riprova ↻
              </button>
            </div>
          )}

          <div className="mt-10 hidden lg:block">
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">Vuoi vedere come appare ai tuoi clienti?</p>
            <LeadCtaButtons />
          </div>
        </div>

        {/* Right: phone mockup */}
        <div className="relative mx-auto" style={{ width: 320 }}>
          <InteractionHint
            visible={phoneInView && !userInteracted}
            text="Tocca un servizio"
            icon={MousePointerClick}
            position="top"
            tone="blue"
            testid="3tap-hint"
          />
          <div className="absolute -inset-8 bg-gradient-to-br from-blue-200/40 via-transparent to-violet-200/40 dark:from-blue-900/30 dark:to-violet-900/30 blur-2xl rounded-full" />
          <div className="relative rounded-[3rem] bg-zinc-900 dark:bg-zinc-950 p-3 shadow-2xl border-[3px] border-zinc-800">
            {/* Notch */}
            <div className="h-6 flex items-center justify-center">
              <div className="h-1.5 w-20 rounded-full bg-zinc-800" />
            </div>
            {/* dark-mode-skip:start — the phone display is a physical mockup
                that must always look like a real smartphone screen (white,
                clean), regardless of the page theme. Tools/linters skip this. */}
            <div className="bg-white rounded-[2.2rem] overflow-hidden" style={{ height: 560 }}>
              {/* Screen header */}
              <div className="bg-zinc-50 px-5 pt-4 pb-3 border-b border-zinc-100">
                <div className="text-[9px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Studio Belle</div>
                <div className="font-display text-base font-semibold text-zinc-900 mt-0.5">Prenota online</div>
                <div className="mt-3 flex gap-1.5" data-testid="3tap-progress">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step - 1 || step === 3 ? "bg-blue-600" : "bg-zinc-200"}`} />
                  ))}
                </div>
              </div>

              {/* Screen content — animated steps */}
              <div className="px-5 pt-4 pb-6 overflow-y-auto" style={{ height: 480 }}>
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      key="step-service"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 mb-2">Scegli un servizio</div>
                      <div className="space-y-2">
                        {SERVICES.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => pick("service", s, 1)}
                            className="w-full text-left p-3 rounded-xl bg-white border border-zinc-200 hover:border-blue-500 hover:bg-blue-50 transition active:scale-[0.98]"
                            data-testid={`3tap-service-${s.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-zinc-900">{s.name}</div>
                              <div className="text-sm font-semibold text-zinc-900">{s.price}</div>
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">{s.duration}</div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {step === 1 && (
                    <motion.div
                      key="step-day"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 mb-2">Quando vuoi venire?</div>
                      <div className="text-xs text-zinc-700 mb-3">Per <strong>{picked.service?.name}</strong></div>
                      <div className="grid grid-cols-3 gap-2">
                        {DAYS.map((d) => (
                          <button
                            key={d}
                            onClick={() => pick("day", d, 2)}
                            className="px-2 py-3 rounded-xl bg-white border border-zinc-200 hover:border-blue-500 hover:bg-blue-50 transition active:scale-[0.96]"
                            data-testid={`3tap-day-${d.replace(" ", "-")}`}
                          >
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{d.split(" ")[0]}</div>
                            <div className="text-lg font-display font-semibold text-zinc-900">{d.split(" ")[1]}</div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step-slot"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="text-[10px] font-bold tracking-widest uppercase text-zinc-400 mb-2">Orari disponibili · {picked.day}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {(SLOTS_BY_DAY[picked.day] || []).map((slot) => (
                          <button
                            key={slot}
                            onClick={() => pick("slot", slot, 3)}
                            className="px-2 py-2.5 rounded-xl bg-white border border-zinc-200 hover:border-blue-500 hover:bg-blue-50 transition text-sm font-semibold text-zinc-900 tabular-nums active:scale-[0.96] dark:border-zinc-800/50 dark:text-zinc-200 dark:bg-blue-950/40"
                            data-testid={`3tap-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step-done"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
                      className="text-center pt-6"
                      data-testid="3tap-done"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
                        className="mx-auto h-20 w-20 rounded-full bg-emerald-500 flex items-center justify-center text-white"
                      >
                        <Check className="h-10 w-10" strokeWidth={3} />
                      </motion.div>
                      <div className="font-display text-xl font-semibold text-zinc-900 mt-5">Prenotato!</div>
                      <div className="text-xs text-zinc-500 mt-1">Hai impiegato solo {seconds} secondi</div>
                      <div className="mt-5 mx-3 p-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-left">
                        <div className="text-[9px] font-bold tracking-widest uppercase text-zinc-400">Riepilogo</div>
                        <div className="text-sm font-semibold text-zinc-900 mt-1.5">{picked.service?.name}</div>
                        <div className="text-xs text-zinc-600 mt-1">
                          {picked.day} alle <strong className="text-zinc-900">{picked.slot}</strong>
                        </div>
                        <div className="text-xs text-zinc-600 mt-0.5">{picked.service?.duration} · {picked.service?.price}</div>
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-4">Demo — nessuna prenotazione reale è stata creata</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            {/* dark-mode-skip:end */}
          </div>
        </div>

        <div className="lg:hidden">
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2 text-center">Vuoi questa esperienza per i tuoi clienti?</p>
          <div className="flex justify-center"><LeadCtaButtons /></div>
        </div>
      </div>
    </section>
  );
}

// =====================================================================
// 2. ROI CALCULATOR
// =====================================================================

function RoiCalculator() {
  const [apptPerWeek, setApptPerWeek] = useState(40);
  const [minutesPerCall, setMinutesPerCall] = useState(4);
  const [hourlyValue, setHourlyValue] = useState(25);
  const [touched, setTouched] = useState(false);
  const [roiRef, roiInView] = useInView(0.4);
  const { markExplored, setRoiSnapshot } = useDemoProgress();
  useEffect(() => { if (roiInView) trackLanding("viewed_roi"); }, [roiInView]);
  const wrap = (setter) => (v) => {
    if (!touched) { setTouched(true); trackLanding("touched_roi"); }
    markExplored("roi");
    setter(v);
  };

  const computed = useMemo(() => {
    // Heuristic baseline: each booking takes ~minutesPerCall to handle by phone/manual.
    // Prenotoo automates ~85% of these touchpoints.
    const totalMinutesWeek = apptPerWeek * minutesPerCall;
    const automatedRate = 0.85;
    const minutesSavedWeek = totalMinutesWeek * automatedRate;
    const hoursSavedWeek = minutesSavedWeek / 60;
    const hoursSavedMonth = hoursSavedWeek * 4.33;
    const hoursSavedYear = hoursSavedWeek * 52;
    const eurMonth = hoursSavedMonth * hourlyValue;
    const eurYear = hoursSavedYear * hourlyValue;
    // Plus: lost revenue from missed calls (avg 8% of attempted bookings give up if line busy)
    const missedRate = 0.08;
    const recoveredApptYear = apptPerWeek * 52 * missedRate;
    return {
      hoursWeek: hoursSavedWeek,
      hoursMonth: hoursSavedMonth,
      hoursYear: hoursSavedYear,
      eurMonth,
      eurYear,
      recoveredApptYear,
    };
  }, [apptPerWeek, minutesPerCall, hourlyValue]);

  // Publish snapshot to context only after the user has touched the sliders,
  // so the celebration card knows whether to personalize or not.
  useEffect(() => {
    if (touched) setRoiSnapshot({ ...computed, apptPerWeek, hourlyValue });
  }, [computed, touched, apptPerWeek, hourlyValue, setRoiSnapshot]);

  const fmt = (n, d = 0) => n.toLocaleString("it-IT", { maximumFractionDigits: d, minimumFractionDigits: d });

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="home-roi-calculator" ref={roiRef}>
      <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-600 mb-3 dark:text-emerald-400 inline-flex items-center gap-1.5">
            <Calculator className="h-3 w-3" /> Quanto vale il tuo tempo?
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium dark:text-zinc-100">
            Calcola quanto<br />
            <em className="not-italic text-emerald-600 dark:text-emerald-400">risparmi davvero</em>.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 mt-4 dark:text-zinc-400">
            Sposta gli slider in base alla tua attività. Numeri reali, basati su clienti veri di Prenotoo.
          </p>

          <div className="mt-8 space-y-5 relative">
            <InteractionHint
              visible={roiInView && !touched}
              text="Trascina gli slider"
              icon={MoveHorizontal}
              position="top-right"
              tone="emerald"
              testid="roi-hint"
            />
            <RoiSlider label="Appuntamenti gestiti a settimana" testid="roi-input-appt" value={apptPerWeek} setValue={wrap(setApptPerWeek)} min={5} max={200} step={5} suffix="" />
            <RoiSlider label="Minuti spesi per ogni telefonata / messaggio" testid="roi-input-minutes" value={minutesPerCall} setValue={wrap(setMinutesPerCall)} min={1} max={10} step={1} suffix=" min" />
            <RoiSlider label="Valore di un'ora del tuo lavoro" testid="roi-input-hourly" value={hourlyValue} setValue={wrap(setHourlyValue)} min={10} max={100} step={5} suffix=" €" prefix="€" />
          </div>
        </div>

        {/* Result panel */}
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-emerald-100/60 via-blue-100/30 to-transparent dark:from-emerald-900/30 dark:via-blue-900/20 blur-3xl rounded-full" />
          <div className="relative bg-zinc-900 dark:bg-zinc-950 text-white rounded-[2rem] p-8 md:p-10 border border-zinc-800 overflow-hidden">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400 mb-2">Il tuo risparmio stimato</div>

            <div className="space-y-6 mt-6">
              <RoiResult
                label="Ore risparmiate al mese"
                value={`${fmt(computed.hoursMonth, 1)} h`}
                subtitle={`pari a ${fmt(computed.hoursMonth / 8, 1)} giornate lavorative`}
                tone="emerald"
                testid="roi-hours-month"
              />
              <RoiResult
                label="Valore tempo recuperato all'anno"
                value={`€ ${fmt(computed.eurYear)}`}
                subtitle={`${fmt(computed.hoursYear)} h di lavoro all'anno`}
                tone="blue"
                big
                testid="roi-eur-year"
              />
              <RoiResult
                label="Appuntamenti recuperati / anno"
                value={`+${fmt(computed.recoveredApptYear)}`}
                subtitle="prenotazioni che non perdi più (chiamate non risposte)"
                tone="violet"
                testid="roi-appt-year"
              />
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-800">
              <div className="text-xs text-zinc-400 mb-3">Vuoi numeri precisi sulla tua attività?</div>
              <LeadCtaButtons variant="dark" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RoiSlider({ label, value, setValue, min, max, step, suffix = "", prefix = "", testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{label}</label>
        <div className="font-display text-2xl tabular-nums text-zinc-900 dark:text-zinc-100">
          {prefix}{value}{suffix}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none bg-zinc-200 dark:bg-zinc-800 cursor-pointer accent-emerald-600"
        style={{ background: `linear-gradient(to right, rgb(5 150 105) 0%, rgb(5 150 105) ${pct}%, rgb(228 228 231) ${pct}%, rgb(228 228 231) 100%)` }}
        data-testid={testid}
      />
      <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
        <span>{prefix}{min}{suffix}</span>
        <span>{prefix}{max}{suffix}</span>
      </div>
    </div>
  );
}

function RoiResult({ label, value, subtitle, tone, big, testid }) {
  const tones = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    violet: "text-violet-400",
  };
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400">{label}</div>
      <motion.div
        key={value}
        initial={{ opacity: 0.3, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`font-display tabular-nums ${tones[tone]} ${big ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"} mt-1 font-medium tracking-tight`}
      >
        {value}
      </motion.div>
      <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>
    </div>
  );
}

// =====================================================================
// 3. BEFORE / AFTER SLIDER
// =====================================================================

function BeforeAfterSlider() {
  const [pct, setPct] = useState(50);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hintExpired, setHintExpired] = useState(false);
  const [baRef, baInView] = useInView(0.4);
  const wrapRef = useRef(null);
  const dragging = useRef(false);
  const { markExplored } = useDemoProgress();
  useEffect(() => { if (baInView) trackLanding("viewed_ba"); }, [baInView]);

  useEffect(() => {
    if (!baInView || hintExpired) return undefined;
    const t = setTimeout(() => setHintExpired(true), 9000);
    return () => clearTimeout(t);
  }, [baInView, hintExpired]);

  const onMove = (clientX) => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(0, Math.min(100, p)));
  };

  const onMouseDown = () => {
    dragging.current = true;
    if (!hasInteracted) { setHasInteracted(true); trackLanding("dragged_ba"); }
    markExplored("ba");
  };
  const onMouseUp = () => { dragging.current = false; };
  const onMouseMove = (e) => { if (dragging.current) onMove(e.clientX); };
  const onTouchMove = (e) => { if (e.touches[0]) onMove(e.touches[0].clientX); };

  useEffect(() => {
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  return (
    <section className="bg-zinc-50 dark:bg-zinc-950 border-y border-zinc-100 dark:border-zinc-800" data-testid="home-before-after" ref={baRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-8 md:mb-12">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-600 mb-3 dark:text-rose-400 inline-flex items-center gap-1.5">
            <GitCompareArrows className="h-3 w-3" /> Prima · Dopo
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium dark:text-zinc-100">
            Trascina lo slider.<br />
            Vedi <em className="not-italic text-rose-600 dark:text-rose-400">la differenza</em>.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 mt-3 dark:text-zinc-400">
            Da sinistra: il caos di chi gestisce tutto a mano. A destra: la stessa giornata su Prenotoo.
          </p>
        </div>

        <div
          ref={wrapRef}
          className="relative aspect-[16/10] sm:aspect-[16/9] rounded-3xl overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl select-none touch-none"
          onMouseDown={(e) => { dragging.current = true; if (!hasInteracted) { setHasInteracted(true); trackLanding("dragged_ba"); } markExplored("ba"); onMove(e.clientX); }}
          onTouchStart={(e) => { dragging.current = true; if (!hasInteracted) { setHasInteracted(true); trackLanding("dragged_ba"); } markExplored("ba"); if (e.touches[0]) onMove(e.touches[0].clientX); }}
          onTouchMove={onTouchMove}
          onTouchEnd={onMouseUp}
          data-testid="before-after-wrap"
        >
          {/* AFTER (background, full) */}
          <AfterAgenda />

          {/* BEFORE (clipped overlay on left) — paper tilts subtly toward
              the slider as it's revealed/hidden, making it feel physical */}
          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}>
            <div
              className="absolute inset-0"
              style={{
                // pct 50 → 0deg, pct 0 → +1.4deg (paper falls right as finger pulls right),
                // pct 100 → -1.4deg (paper rises left). Slight upward translate for "lift".
                transform: `rotate(${((50 - pct) / 50) * 1.4}deg) translateY(${Math.abs(50 - pct) / 50 * -2}px)`,
                transformOrigin: pct < 50 ? "left center" : "right center",
                transition: dragging.current ? "none" : "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
                willChange: "transform",
              }}
            >
              <BeforeAgenda />
            </div>
          </div>

          {/* Divider + handle */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)] z-20 cursor-ew-resize"
            style={{ left: `${pct}%` }}
            onMouseDown={onMouseDown}
            onTouchStart={onMouseDown}
          />
          <button
            type="button"
            onMouseDown={onMouseDown}
            onTouchStart={onMouseDown}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-30 h-12 w-12 rounded-full bg-white shadow-2xl flex items-center justify-center cursor-ew-resize border-2 border-zinc-200 hover:scale-110 transition dark:border-zinc-800/50"
            style={{ left: `${pct}%` }}
            aria-label="Trascina per confrontare"
            data-testid="before-after-handle"
          >
            <GitCompareArrows className="h-5 w-5 text-zinc-700 dark:text-zinc-300" />
          </button>

          {/* Hint pinned above the handle, follows pct */}
          <AnimatePresence>
            {baInView && !hasInteracted && !hintExpired && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.9 }}
                animate={{
                  opacity: 1, y: 0, scale: 1,
                  rotate: [0, -2.5, 2.5, -1.5, 1.5, 0],
                }}
                transition={{
                  opacity: { duration: 0.3 },
                  y: { duration: 0.3 },
                  scale: { duration: 0.3 },
                  rotate: { duration: 1.6, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" },
                }}
                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.25 } }}
                className="absolute z-30 -translate-x-1/2 px-3 py-1.5 rounded-full bg-zinc-900 text-white text-[11px] font-semibold tracking-wide inline-flex items-center gap-1.5 whitespace-nowrap pointer-events-none shadow-lg"
                style={{ left: `${pct}%`, top: "calc(50% - 56px)" }}
                data-testid="before-after-hint"
              >
                <MoveHorizontal className="h-3.5 w-3.5" />
                <span>Trascinami</span>
                {/* tiny arrow pointer */}
                <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45 bg-zinc-900" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Labels */}
          <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold tracking-widest uppercase shadow-lg">
            Prima
          </div>
          <div className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold tracking-widest uppercase shadow-lg">
            Con Prenotoo
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:gap-6 text-sm" data-testid="before-after-stats">
          <div className="rounded-2xl bg-white dark:bg-zinc-900 border border-rose-200 dark:border-rose-900/50 p-4 md:p-5">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-rose-600 dark:text-rose-400 mb-1">Senza Prenotoo</div>
            <ul className="text-xs md:text-sm text-zinc-700 dark:text-zinc-300 space-y-1.5 mt-2">
              <li className="flex gap-2"><AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" /> Telefonate continue, anche fuori orario</li>
              <li className="flex gap-2"><AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" /> Doppie prenotazioni e dimenticanze</li>
              <li className="flex gap-2"><AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" /> Post-it e quaderno illegibili</li>
              <li className="flex gap-2"><AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" /> Slot vuoti che pesano sul fatturato</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-white dark:bg-zinc-900 border border-emerald-200 dark:border-emerald-900/50 p-4 md:p-5">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-600 dark:text-emerald-400 mb-1">Con Prenotoo</div>
            <ul className="text-xs md:text-sm text-zinc-700 dark:text-zinc-300 space-y-1.5 mt-2">
              <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Clienti prenotano da soli, 24/7</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Niente sovrapposizioni: il sistema le blocca</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Agenda chiara, ovunque, in tempo reale</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> Lista d'attesa e auto-fill riempiono i buchi</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function BeforeAgenda() {
  // dark-mode-skip:start — paper is always paper; the page theme should not
  // turn it dark amber. Linters/auto-fixers skip this whole subtree.
  // Realistic-feeling messy paper agenda — no emoji, no perfect typography.
  // Hand-written entries with strikethrough, inconsistent spacing, ink smudges.
  const entries = [
    { time: "9.00", t: "Anna manicure", strike: false, rotate: -0.3 },
    { time: "9.30", t: "Sara taglio + piega", strike: false, rotate: 0.2 },
    { time: "10", t: "Lucia (??)", strike: true, rotate: -0.4 },
    { time: "10.30", t: "Marta tinta - chiamare", strike: false, rotate: 0.3 },
    { time: "11", t: "Sara/Lucia ↑↑ vedere", strike: false, rotate: -0.2, warn: true },
    { time: "12", t: "spostato", strike: true, rotate: 0.1 },
    { time: "14.30", t: "Roberta -- anticipa?", strike: false, rotate: 0.4 },
    { time: "15", t: "Giulia pedicure", strike: false, rotate: -0.3 },
    { time: "16", t: "Susi pulizia viso", strike: false, rotate: 0.2 },
    { time: "17.30", t: "libero", strike: false, rotate: -0.1, faded: true },
  ];
  const handFont = "'Caveat', 'Bradley Hand', 'Segoe Script', cursive";
  return (
    <div className="absolute inset-0 bg-amber-50 overflow-hidden">
      {/* Lined paper */}
      <div
        className="absolute inset-0 opacity-40"
        style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0, transparent 27px, rgba(120,53,15,0.35) 28px)" }}
      />
      {/* Red margin line */}
      <div className="absolute top-0 bottom-0 left-12 w-px bg-rose-400/50" />

      {/* Coffee stain — irregular blob via radial-gradient */}
      <div
        className="absolute"
        style={{
          top: "62%",
          right: "18%",
          width: 110,
          height: 90,
          background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(120,53,15,0.28) 0%, rgba(120,53,15,0.18) 40%, rgba(120,53,15,0.05) 70%, transparent 100%)",
          transform: "rotate(-15deg)",
          filter: "blur(2px)",
        }}
      />
      <div
        className="absolute"
        style={{
          top: "60%",
          right: "22%",
          width: 60,
          height: 50,
          background: "radial-gradient(ellipse 60% 60% at 40% 50%, rgba(120,53,15,0.22) 0%, rgba(120,53,15,0.08) 60%, transparent 100%)",
          transform: "rotate(20deg)",
          filter: "blur(1.5px)",
        }}
      />

      {/* Ink smudge in top-right */}
      <div
        className="absolute top-3 right-8 w-6 h-3 rounded-full opacity-50"
        style={{ background: "radial-gradient(ellipse, rgba(30,30,80,0.4), transparent)", transform: "rotate(35deg)" }}
      />

      {/* Top header (handwritten) */}
      <div
        className="absolute top-3 left-4 right-4 flex items-baseline justify-between"
        style={{ fontFamily: handFont }}
      >
        <div className="text-[22px] sm:text-[26px] leading-none text-zinc-800" style={{ transform: "rotate(-0.5deg)" }}>
          lun&nbsp;17 feb
        </div>
        <div className="text-sm text-zinc-700" style={{ transform: "rotate(0.3deg)" }}>
          appuntamenti
        </div>
      </div>

      {/* Schedule entries */}
      <div className="absolute top-16 left-14 right-6 space-y-[3px]" style={{ fontFamily: handFont }}>
        {entries.map((e, i) => (
          <div
            key={i}
            className="flex items-baseline gap-3 text-zinc-900"
            style={{ transform: `rotate(${e.rotate}deg)`, fontSize: "17px", lineHeight: "26px" }}
          >
            <span className="w-12 shrink-0 text-zinc-700">{e.time}</span>
            <span
              className={`flex-1 ${e.faded ? "text-zinc-500" : ""}`}
              style={e.strike ? { textDecoration: "line-through", textDecorationColor: "rgba(140,30,30,0.7)", textDecorationThickness: "2px", color: "rgba(60,60,60,0.55)" } : undefined}
            >
              {e.t}
              {e.warn && <span className="ml-2 text-rose-700 text-[15px]">!!</span>}
            </span>
          </div>
        ))}
      </div>

      {/* Margin scribble */}
      <div
        className="absolute"
        style={{
          left: 4,
          top: "44%",
          fontFamily: handFont,
          fontSize: 13,
          color: "rgba(140,30,30,0.85)",
          transform: "rotate(-90deg)",
          transformOrigin: "left top",
        }}
      >
        chiamare
      </div>

      {/* Bottom-left scribble note (handwritten) */}
      <div
        className="absolute bottom-4 left-14 right-6 text-zinc-700"
        style={{ fontFamily: handFont, fontSize: 15, lineHeight: "20px", transform: "rotate(-0.4deg)" }}
      >
        <div>chiamare lavanderia</div>
        <div className="text-zinc-500" style={{ transform: "rotate(0.2deg)" }}>ricordarsi cassa</div>
      </div>

      {/* Pen mark / underline near top */}
      <svg className="absolute top-12 left-14 opacity-60 pointer-events-none" width="120" height="6" viewBox="0 0 120 6">
        <path d="M2,3 Q30,1 60,3 T118,3" stroke="rgba(30,30,80,0.6)" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      </svg>
    </div>
    /* dark-mode-skip:end */
  );
}

function AfterAgenda() {
  // Clean agenda
  const blocks = [
    { time: "09:00", who: "Sara", svc: "Manicure", color: "bg-blue-500", op: "S" },
    { time: "09:45", who: "Lucia", svc: "Pedicure", color: "bg-violet-500", op: "L" },
    { time: "11:00", who: "Marta", svc: "Pulizia viso", color: "bg-emerald-500", op: "M" },
    { time: "12:30", who: "Anna", svc: "Massaggio", color: "bg-amber-500", op: "A" },
    { time: "14:00", who: "Giulia", svc: "Manicure", color: "bg-pink-500", op: "G" },
    { time: "15:30", who: "Roberta", svc: "Tinta", color: "bg-cyan-500", op: "R" },
  ];
  return (
    <div className="absolute inset-0 bg-white dark:bg-zinc-900 p-4 sm:p-6 overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400">Agenda · Lun 17 Feb</div>
          <div className="font-display text-base sm:text-lg font-semibold text-zinc-900 dark:text-zinc-100">8 prenotazioni · 0 conflitti</div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold">
          <Sparkles className="h-3 w-3" /> Tutto sincronizzato
        </div>
      </div>

      {/* Calendar grid */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-zinc-50/50 dark:bg-zinc-800/40">
        {blocks.map((b, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="flex items-center gap-3 px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 last:border-0"
          >
            <div className="text-[11px] font-mono tabular-nums text-zinc-500 dark:text-zinc-400 w-12">{b.time}</div>
            <div className={`h-7 w-7 rounded-full ${b.color} text-white flex items-center justify-center text-[11px] font-semibold shadow-sm`}>
              {b.op}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs sm:text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{b.who}</div>
              <div className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 truncate">{b.svc}</div>
            </div>
            <ChevronRight className="h-3 w-3 text-zinc-300 dark:text-zinc-600 hidden sm:block" />
          </motion.div>
        ))}
      </div>

      {/* Footer KPI strip */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[
          { l: "Confermate", v: "8", c: "text-emerald-600 dark:text-emerald-400" },
          { l: "Lista d'attesa", v: "2", c: "text-violet-600 dark:text-violet-400" },
          { l: "Auto-fill", v: "+3", c: "text-blue-600 dark:text-blue-400" },
        ].map((k) => (
          <div key={k.l} className="rounded-lg bg-zinc-50 dark:bg-zinc-800/60 px-2 py-1.5 text-center">
            <div className="text-[8px] font-bold tracking-widest uppercase text-zinc-500 dark:text-zinc-400">{k.l}</div>
            <div className={`font-display text-base sm:text-lg ${k.c} tabular-nums`}>{k.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
